import _ from "lodash";

import { TABLE_AGGREGATOR, TColumnType } from "components/Table/model";
import { ITableAction } from "model/application/Table";
import { ACTION_TYPE } from "model/application/UIActionTypes";
import { IClient } from "model/entities/Client";
import { TErrorDetails } from "redux/actions/actionErrors";

import { isWorkspaceTemplate } from "./clients/utils";

export const transposeLocalTimeToUTCTime = (localTime: Date): Date => {
  const timezoneOffset = new Date(localTime).getTimezoneOffset();

  const transposedDate = new Date(
    new Date(localTime).getTime() - timezoneOffset * 1000 * 60,
  );
  return transposedDate;
};

/**
 * Checks if the phone number is valid
 * @param {String} phone
 * @returns {Boolean} returns True if the phone number is valid
 */
export function isPhoneValid(phone: string, phoneRegex?: string): boolean {
  if (phoneRegex) {
    const regex = new RegExp(phoneRegex);
    return regex.test(phone);
  }
  // eslint-disable-next-line sonarjs/concise-regex -- legacy warning, to review
  return /^[+]+[0-9]{4,17}$/i.test(phone);
}
/**
 * Checks if the email is valid
 * @param {String} email
 * @returns {Boolean} returns True if the email is valid
 */
export function isEmailValid(email: string, emailRegex?: string): boolean {
  if (emailRegex) {
    const regex = new RegExp(emailRegex);
    return regex.test(email);
  }
  return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i.test(email);
}
/**
 * Checks if the string has only alphanumerical and underscore character
 * @param {String} text
 */
export function isAlphanumericalWithUnderscores(text: string): boolean {
  const regexp = /^[a-z0-9_]*$/;
  return regexp.test(text);
}

/**
 * Checks if the string has only alphanumerical and underscore character
 * @param {String} text
 */
export function isAlphanumericalWithoutUnderscores(text: string): boolean {
  const regexp = /^[a-z0-9]*$/;
  return regexp.test(text);
}

/**
 * Validates a URL path–segment style id: only letters, digits, hyphen, underscore (anything else is invalid).
 * Error copy: `components.inputErrors.invalidCustomId`.
 */
export function isUrlSafeIdentifier(text: string): boolean {
  return /^[a-zA-Z0-9_-]*$/.test(text);
}

/**
 * Checks if the string has only alphanumerical or underscore characters
 * (and starts with a letter)
 * @param {String} text
 */
export function isAlphanumericalStartingWithLetter(text: string): boolean {
  const regexp = /^[a-z][a-z0-9_]*$/;
  return regexp.test(text);
}

/**
 * Checks if the string has only alphanumerical and underscore character
 * (and starts with underscore)
 * @param {String} text
 */
export function isAlphanumericalStartingWithUnderscore(text: string): boolean {
  const regexp = /^_[a-z0-9_]*$/;
  return regexp.test(text);
}

/**
 * Filters the values and tests the
 * @param {String} value Value to test
 * @param {String} pattern Pattern to test for
 * @returns {Boolean} True if the pattern matches False otherwise
 */
export function regexTestValue(value: string, pattern: string): boolean {
  const re = new RegExp(pattern, "gi");
  return re.test(value);
}
/**
 * Convert agiven Base64 token string to object
 * @param {String} val
 * @returns {Object} JSON parsed object
 */
export const unBase64ToObj = (val: string): unknown => {
  const str = Buffer.from(val, "base64").toString("utf-8");
  return JSON.parse(str);
};

/**
 * Convert data in CSV (comma separated value) format to a javascript array.
 *
 * Values are separated by a comma, or by a custom one character delimeter.
 * Rows are separated by a new-line character.
 *
 * Leading and trailing spaces and tabs are ignored.
 * Values may optionally be enclosed by double quotes.
 * Values containing a special character (comma's, double-quotes, or new-lines)
 *   must be enclosed by double-quotes.
 * Embedded double-quotes must be represented by a pair of consecutive
 * double-quotes.
 *
 * Example usage:
 *   var csv = '"x", "y", "z"\n12.3, 2.3, 8.7\n4.5, 1.2, -5.6\n';
 *   var array = csv2array(csv);
 *
 * Author: Jos de Jong, 2010
 *
 * @param {string} data      The data in CSV format.
 * @param {string} delimeter [optional] a custom delimeter. Comma ',' by default
 *                           The Delimeter must be a single character.
 * @return {Array} array     A two dimensional array containing the data
 * @throw {String} error     The method throws an error when there is an
 *                           error in the provided data.
 */
export const csv2array = (data: string, delimeter: string): string[][] => {
  // Retrieve the delimeter
  if (!data) return [];
  if (delimeter === undefined || delimeter === null) delimeter = ",";
  if (delimeter?.length > 1) delimeter = ",";

  // initialize variables
  const newline = "\n";
  const eof = "";
  let i = 0;
  let c = data?.charAt(i) ?? "";
  let row = 0;
  const array: any[] = [];

  while (c !== eof) {
    // skip whitespaces
    while (c === " " || c === "\t" || c === "\r") {
      c = data?.charAt(++i) ?? ""; // read next char
    }

    // get value
    let value: any = "";
    if (c === '"') {
      // value enclosed by double-quotes
      c = data?.charAt(++i);

      do {
        if (c !== '"') {
          // read a regular character and go to the next character
          value += c;
          c = data?.charAt(++i);
        }

        if (c === '"') {
          // check for escaped double-quote
          const cnext = data?.charAt(i + 1);
          if (cnext === '"') {
            // this is an escaped double-quote.
            // Add a double-quote to the value, and move two characters ahead.
            value += '"';
            i += 2;
            c = data?.charAt(i) ?? "";
          }
        }
      } while (c !== eof && c !== '"');

      if (c === eof) {
        throw new Error("Unexpected end of data, double-quote expected");
      }

      c = data?.charAt(++i) ?? "";
    } else {
      // value without quotes
      while (
        c !== eof &&
        c !== delimeter &&
        c !== newline &&
        c !== "\t" &&
        c !== "\r"
      ) {
        value += c;
        c = data?.charAt(++i) ?? "";
      }
    }

    // add the value to the array
    if (array.length <= row) array.push([]);
    array[row].push(value);

    // skip whitespaces
    while (c === " " || c === "\t" || c === "\r") {
      c = data?.charAt(++i) ?? "";
    }

    // go to the next row or column
    if (c === delimeter) {
      // to the next column
      //col++;
    } else if (c === newline) {
      // to the next row
      //col = 0;
      row++;
    } else if (c !== eof) {
      // unexpected character
      throw new Error("Delimiter expected after character " + i);
    }

    // go to the next character
    c = data?.charAt(++i) ?? "";
  }
  // special case of the last cell being empty:
  if (array[0].length !== array[array.length - 1].length) {
    array[array.length - 1][array[0].length - 1] = "";
  }
  return array;
};

export const clone = (obj: any): any => {
  let copy: any;

  // Handle the 3 simple types, and null or undefined
  if (null == obj || "object" != typeof obj) return obj;

  // Handle Date
  if (obj instanceof Date) {
    copy = new Date();
    copy.setTime(obj.getTime());
    return copy;
  }

  // Handle Array
  if (obj instanceof Array) {
    copy = [] as unknown[];
    for (let i = 0, len = obj.length; i < len; i++) {
      copy[i] = clone(obj[i]);
    }
    return copy;
  }

  if (obj.hasOwnProperty("file") && obj.hasOwnProperty("fileName")) {
    if (obj.file instanceof File) {
      return {
        file: new File([obj.file], obj.fileName, { type: obj.file.type }),
        fileName: obj.fileName,
      };
    } else {
      return { file: [], fileName: "" };
    }
  }

  // Handle Object
  if (obj instanceof Object) {
    copy = {} as Record<string, unknown>;
    for (const attr in obj) {
      if (obj.hasOwnProperty(attr)) copy[attr] = clone(obj[attr]);
    }
    return copy;
  }

  throw new Error("Unable to copy obj! Its type isn't supported.");
};

export const formatTemplateString = (
  pattern: string | undefined,
  element: {
    [key: string]: string;
  },
): string => {
  if (!pattern && !element.label) return "/!\\ No Name";
  if (!pattern) return element.label;
  let result = "";
  const splittedPattern = pattern?.split("$") ?? [];
  for (let i = 0; i < splittedPattern.length; i++) {
    if (i % 2 === 1) {
      // even idx: we replace the string per the value of the attribute
      // if there is "." char, we split
      const attrs = splittedPattern[i].split(".");
      switch (attrs.length) {
        case 1:
          result += element[splittedPattern[i]];
          break;
        case 2:
          // @ts-expect-error TS7053
          result += element[attrs[0]][attrs[1]];
          break;
        case 3:
          // @ts-expect-error TS7053
          result += element[attrs[0]][attrs[1]][attrs[2]];
          break;
        default: /** nothing */
      }
    } else {
      // odd idx: we display the string as it is
      result += splittedPattern[i];
    }
  }
  if (result?.length === 0) {
    return "/!\\ No Name";
  }
  return result;
};

const chars = "abcdefghijklmnopqrstuvwxyz";

export const generateRandomId = (): string => {
  let currentMsIndex = 0;
  const currentTimestamp = Date.now();
  // eslint-disable-next-line sonarjs/pseudo-random -- legacy warning, to review
  currentMsIndex = 1e6 + Math.floor(Math.random() * 100000);
  currentMsIndex += 111;
  const id1 = currentTimestamp.toString(36) + currentMsIndex?.toString(36);
  const id2 = id1.split("").reverse().join("");
  let prefix = "";
  for (let i = 0; i < 4; i++) {
    // eslint-disable-next-line sonarjs/pseudo-random -- legacy warning, to review
    prefix += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return prefix + id2;
};

export const aggregateValues = (
  values: any[],
  aggregator: TABLE_AGGREGATOR,
) => {
  switch (aggregator) {
    case TABLE_AGGREGATOR.SUM: {
      return values.reduce(
        (acc: number, curr: any) => (isNaN(curr) ? acc : acc + curr),
        0,
      );
    }
    case TABLE_AGGREGATOR.MEAN: {
      if (values.length === 0) return 0;
      return (
        values.reduce(
          (acc: number, curr: any) => (isNaN(curr) ? acc : acc + curr),
          0,
        ) / values.length
      );
    }
    case TABLE_AGGREGATOR.MIN: {
      const min = values.reduce((acc: number, curr: any) => {
        if (isNaN(curr)) {
          return acc;
        }
        return acc > curr ? curr : acc;
      }, Infinity);
      return min === Infinity ? 0 : min;
    }
    case TABLE_AGGREGATOR.MAX: {
      const max = values.reduce((acc: number, curr: any) => {
        if (isNaN(curr)) {
          return acc;
        }
        return acc < curr ? curr : acc;
      }, -Infinity);
      return max === -Infinity ? 0 : max;
    }
    default: {
      return values[0];
    }
  }
};

/**
 * @details Example
 *
 * {
 *   "code": "42P01",
 *   "file": "parse_relation.c",
 *   "internalPosition": "8",
 *   "internalQuery": "UPDATE l_ SET _updated_at = NOW() WHERE _id = $1",
 *   "length": 253,
 *   "line": "1395",
 *   "name": "error",
 *   "routine": "parserOpenTable",
 *   "severity": "ERROR",
 *   "where": "PL/pgSQL function update_list_updated_at_timestamp_on_items_teams_change_function() line 7 at EXECUTE",
 *   "message": "relation \"l_\" does not exist",
 *   "stack": "error: relation \"l_\" does not exist\n   at async Pg.query (/usr/src/app/dist/core/database/postgres/Pg.js:67:28)\n    at async Db.query (/usr/src/app/dist/core/database/postgres/Db.js:455:16)\n    (...)    at async /usr/src/app/dist/core/notifications/internal/sentry/init.notest.js:126:21"
 *  }
 */
export const formatSQLString = (details?: TErrorDetails): string => {
  if (typeof details !== "object") {
    return details ? `${details}` : "";
  }
  const {
    constraint,
    detail,
    table,
    line,
    where,
    internalPosition,
    internalQuery,
    stack,
  } = details;
  const result = [];

  if (detail) result.push(`<b>Detail</b>: ${detail}`);
  if (table) result.push(`<b>Table</b>: ${table}`);
  if (constraint) result.push(`<b>Constraint</b>: ${constraint}`);
  if (line) result.push(`<b>Line</b>: ${line}`);
  if (where) result.push(`<b>Where</b>: ${where}`);
  if (internalPosition) result.push(`<b>Position</b>: ${internalPosition}`);
  if (internalQuery) result.push(`<b>Query</b>: ${internalQuery}`);
  if (stack) result.push(`<b>Stack</b>: ${stack}`);

  return `${result.join("<br><br>")}`;
};

type THeaderActions = {
  downloadAction?: ITableAction;
  createAction?: ITableAction;
  createMultipleAction?: ITableAction;
  tableActions: ITableAction[];
};

export const extractHeaderActions = (
  actions: ITableAction[],
): THeaderActions => {
  const downloadAction = _.find(actions, { actionType: ACTION_TYPE.DOWNLOAD });
  const createAction = _.find(actions, {
    actionType: ACTION_TYPE.CREATE,
  });

  let createMultipleAction = _.find(actions, {
    actionType: ACTION_TYPE.CREATE_MULTIPLE,
  });

  if (!createMultipleAction) {
    const bulkOperationAction = _.find(actions, {
      actionType: ACTION_TYPE.BULK_OPERATION,
    });

    createMultipleAction = _.find(bulkOperationAction?.subOptions, {
      actionType: ACTION_TYPE.CREATE_MULTIPLE,
    }) as ITableAction | undefined;
  }

  const tableActions = _.reject(actions, (action) => {
    return _.includes(
      [ACTION_TYPE.DOWNLOAD, ACTION_TYPE.CREATE, ACTION_TYPE.CREATE_MULTIPLE],
      action.actionType,
    );
  });

  return {
    downloadAction,
    createAction,
    createMultipleAction,
    tableActions,
  };
};

export const hasCreatePermission = (actions: any[]): boolean => {
  const actionsFilter = _.filter(actions, (act) =>
    _.includes(
      [ACTION_TYPE.CREATE, ACTION_TYPE.CREATE_MULTIPLE],
      act.actionType,
    ),
  );
  return _.size(actionsFilter) > 0;
};

export type TSheetData = {
  name: string;
  rows: string[][];
};

export async function downloadInMultipleSheetUtils(
  data: TSheetData[],
  fileNameWithExtension: string,
): Promise<void> {
  try {
    const xlsxModule = await import("xlsx");
    // CJS interop: Vite may wrap exports under .default
    const { utils, writeFile } = xlsxModule.default ?? xlsxModule;

    // Create a new empty Excel workbook
    const wb = utils.book_new();

    // Add each rows in his sheet in the excel file
    _.map(data, (dat) => {
      const sheet = utils.aoa_to_sheet(dat.rows);

      // The maximum size of sheet names in Excel is 32 characters
      const sheetName: string =
        _.size(dat.name) <= 31 ? dat.name : dat.name.slice(-31);

      //4th parameter permit to increment a sheet name if it exist already
      utils.book_append_sheet(wb, sheet, sheetName, true);
    });

    // Download the Excel file
    writeFile(wb, fileNameWithExtension);
  } catch (error) {
    console.error(error);
    return;
  }
}

// TODO: duplicate definition in project
export function hexToRgb(hex: string): string {
  // Delete character # if there is present
  const safeHex = hex?.replace(/^#/, "") ?? "";

  // Separate the hexadecimal chain into three parts (R, G and B)
  const r = parseInt(safeHex.slice(0, 2), 16);
  const g = parseInt(safeHex.slice(2, 4), 16);
  const b = parseInt(safeHex.slice(4, 6), 16);

  // Turn the color in RGB format
  return `rgb(${r}, ${g}, ${b})`;
}

export const TEST_APP_VERSION = "1927-01-01T-00-00";

export const getAppVersion = (): string => {
  if (window._env_?.NODE_ENV === "test") {
    return TEST_APP_VERSION;
  }

  const versionText = window._env_?.SMALA_VERSION;
  if (!versionText) {
    return "";
  }
  const index = versionText.indexOf("20");
  const version = versionText.substring(index);
  return version;
};

/**
 * Returns true if the element is marked as "from_template"
 * (but not in a workspace template itself; originally, workspace templates had the from_template flag set to true)
 */
export const isFromTemplate = (element: object, client: IClient): boolean => {
  // Double check on nature of client
  if (isWorkspaceTemplate(client)) {
    return false;
  }
  // @ts-expect-error TS7053
  return !!element["from_template"];
};

export const stringToBoolean = (str: string | boolean): boolean | undefined => {
  if (_.isBoolean(str)) return str;
  if (str && str.toLowerCase().trim() === "true") return true;
  if (str && str.toLowerCase().trim() === "false") return false;
  return undefined;
};

export const MAX_CHARACTERS_ALLOWED = 25;

export const isLengthOverLimit = (
  character?: string,
  limit = MAX_CHARACTERS_ALLOWED,
): boolean => {
  return _.size(character) > limit;
};

export const areArraysEqual = (arr1: string[], arr2: string[]): boolean => {
  return (
    JSON.stringify(arr1.sort((a, b) => a.localeCompare(b))) ===
    JSON.stringify(arr2.sort((a, b) => a.localeCompare(b)))
  );
};

export const combineDefaultAndStoredColumns = (
  defaultColumnTypes: TColumnType[],
  storedColumnTypes?: TColumnType[],
): TColumnType[] => {
  const formattedColumns = _.map(defaultColumnTypes, (column) => {
    const storedColumn = _.find(
      storedColumnTypes ?? [],
      (col) => col?.name === column?.name,
    );

    if (storedColumn) {
      return {
        ...storedColumn,
        ...column,
        hidden: storedColumn.hidden,
      };
    }

    return column;
  });

  return _.sortBy(_.compact(formattedColumns), (col) => {
    const idx = _.findIndex(storedColumnTypes, {
      name: col.name,
    });
    return idx >= 0 ? idx : Number.MAX_SAFE_INTEGER;
  });
};
